'use client';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface QACustomProps {
  color?: 'green' | 'blue' | 'violet';
  title: ReactNode | SerializedEditorState;
  faqs?: {
    question: ReactNode | SerializedEditorState;
    answer: ReactNode | SerializedEditorState;
  }[];
}

export default function QACustom({ color = 'green', title, faqs }: QACustomProps) {
  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';

  return (
    <div data-meta="QACustom" className="mx-auto max-w-7xl">
      <div className="mx-auto py-6 sm:py-6 lg:py-12">
        <div className="mx-auto divide-y divide-gray-900/10">
          <div className="container max-w-7xl mx-auto py-6 sm:py-4">
            <div className="mx-auto text-center">
              <CustomRichText
                data={title}
                tag="h2"
                className="text-3xl font-bold tracking-tight text-white sm:text-6xl mb-4 sm:mb-8"
              />
            </div>
          </div>
          <div className="flex flex-col bg-white rounded-3xl px-10 py-6 mt-10 space-y-2 divide-y divide-gray-900/10">
            {faqs &&
              faqs.map((faq, index) => (
                <Disclosure key={index} as="div" className="pt-6">
                  <div>
                    <DisclosureButton className="group flex w-full items-start justify-between text-left text-gray-900">
                      <CustomRichText
                        data={faq.question}
                        tag="div"
                        className="text-base font-semibold leading-7"
                      />
                      <span
                        className={`ml-6 p-1 flex items-center rounded-full ${backgroundColorClass}`}
                      >
                        <PlusIcon
                          aria-hidden="true"
                          className="text-white h-8 w-8 group-data-[open]:hidden"
                        />
                        <XMarkIcon
                          aria-hidden="true"
                          className="text-white h-8 w-8 [.group:not([data-open])_&]:hidden"
                        />
                      </span>
                    </DisclosureButton>
                  </div>
                  <DisclosurePanel
                    as="div"
                    unmount={false}
                    className="mt-2 pr-12 rich-text-container"
                  >
                    <CustomRichText
                      data={faq.answer}
                      tag="div"
                      className="text-base leading-7 text-gray-600"
                    />
                  </DisclosurePanel>
                </Disclosure>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
